import React, { useEffect, useRef } from "react";
import { Route, Routes } from "react-router-dom";
import Navbar from "./components/Navbar";
import NotificationToastStack from "./components/NotificationToastStack";
import NewDocumentSpeedDial from "./components/NewDocumentSpeedDial";
import Dashboard from "./pages/Dashboard";
import Home from "./pages/Home";
import GestionTiers from "./pages/GestionTiers";
import GestionEmploye from "./pages/GestionEmploye";
import { Box } from "@mui/material";
import { TypeTiers } from "./constants/enums";
import GestionGropueBien from "./pages/GestionGroupeBien";
import GestionBien from "./pages/GestionBien";
import GestionContratLocation from "./pages/GestionContratLocation";
import GestionArticle from "./pages/GestionArticle";
import GestionArticleMasse from "./pages/GestionArticleMasse";
import DocumentVenteListPage from "./pages/DocumentVenteListPage";
import DocumentVenteDetailPage from "./pages/DocumentVenteDetailPage";
import DocumentVenteCreatePage from "./pages/DocumentVenteCreatePage";
import Comptes from "./pages/Comptes";
import GestionCaisse from "./pages/GestionCaisse";
import GestionOperationDiverse from "./pages/GestionOperationDiverse";
import PointOfSale from "./pages/PointOfSale";
import GestionOptions from "./pages/GestionOptions";
import GestionAiTokens from "./pages/GestionAiTokens";
import GestionGeneriqueData from "./pages/GestionGeneriqueData";
import StockAndStat from "./pages/StocksAndStat";
import GestionDepartement from "./pages/GestionDepartement";
import GestionRoles from "./pages/GestionRoles";
import GestionUserDroits from "./pages/GestionUserDroits";
import GestionUtilisateurs from "./pages/GestionUtilisateurs";
import GestionReportTemplate from "./pages/GestionReportTemplate";
import GestionReportJob from "./pages/GestionReportJob";
import GestionWorkflow from "./pages/GestionWorkflow";
import GestionWorkflowStatuts from "./pages/GestionWorkflowStatuts";
import GestionImmobilisations from "./pages/GestionImmobilisations";
import DepartementSelectionPage from "./pages/DepartementSelectionPage";
import AppBootScreen from "./components/AppBootScreen";
import DeptRequiredRoute from "./components/DeptRequiredRoute";
import AllModeRequiredRoute from "./components/AllModeRequiredRoute";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage  from "./pages/ResetPasswordPage";
import PrivateRoute from "./components/PrivateRoute";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "./reduxStore/Store";
import { setSelectedTiers, clearTiersData } from "./reduxStore/TiersSlice";
import { setSelectedDocument } from "./reduxStore/documentVenteSlice";
import { setSelectedReglement } from "./reduxStore/ReglementSlice";
import { setSelectedGeneriqueData } from "./reduxStore/GeneriqueDataSlice";
import { fetchOptions } from "./reduxStore/OptionSlice";
import { initSocket, disconnectSocket, joinDept, leaveDept } from "./websocket/WebSocketClient";
import { AuthStorageData } from "./models/models";
import { useDepartementBootstrap } from "./hooks/useDepartementBootstrap";

const App: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { selectedDepartement, isAllMode } = useSelector((state: RootState) => state.AppSlice);
  const { isAuthenticated } = useSelector((state: RootState) => state.AuthSlice);
  const { resolved: deptContextResolved, isMonoDepartement } = useDepartementBootstrap();
  const isFirstRender = useRef(true);
  const prevDeptIdRef = useRef<number | null>(null);

  // ── Init / teardown WebSocket selon l'état d'authentification ───
  useEffect(() => {
    if (isAuthenticated) {
      const raw  = localStorage.getItem('auth_data');
      const auth: AuthStorageData | null = raw ? JSON.parse(raw) : null;
      if (auth?.user?.owner_id && auth?.user?.id && auth?.token) {
        initSocket({
          owner_id: auth.user.owner_id,
          user_id:  auth.user.id,
          token:    auth.token,
        });
      }
      // Charge les options tenant au login — disponibles via useOption()
      dispatch(fetchOptions({ departement_id: null }));
    } else {
      disconnectSocket();
    }
  }, [isAuthenticated, dispatch]);

  // ── Rejoindre / quitter la room département + recharger options ──
  useEffect(() => {
    if (!isAuthenticated) return;  // pas d'effet si non connecté (évite fetch /options sur page login)
    if (prevDeptIdRef.current !== null) {
      leaveDept(prevDeptIdRef.current);
    }
    if (selectedDepartement?.id) {
      joinDept(selectedDepartement.id);
      prevDeptIdRef.current = selectedDepartement.id;
      // Charge la vue résolue des options pour ce dept (cascade dept → owner → default)
      dispatch(fetchOptions({ departement_id: selectedDepartement.id }));
    }
  }, [selectedDepartement, dispatch, isAuthenticated]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Réinitialise tous les éléments sélectionnés au changement de département
    dispatch(setSelectedTiers(null));
    dispatch(clearTiersData());
    dispatch(setSelectedDocument(null));
    dispatch(setSelectedReglement(null));
    dispatch(setSelectedGeneriqueData(null));
  }, [selectedDepartement, dispatch]);

  // Routes publiques accessibles sans authentification
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login"           element={<LoginPage />} />
        <Route path="/register"        element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password"  element={<ResetPasswordPage />} />
        <Route path="*"                element={<LoginPage />} />
      </Routes>
    );
  }

  // Authentifié mais ni dept sélectionné, ni mode ALL choisi
  if (!selectedDepartement && !isAllMode) {
    // Le temps de savoir si l'utilisateur est mono-département (auto-sélection
    // en cours via useDepartementBootstrap) — évite un flash du sélecteur.
    if (!deptContextResolved || isMonoDepartement) {
      return <AppBootScreen />;
    }
    return (
      <Routes>
        <Route path="/departement" element={<GestionDepartement />} />
        <Route path="*"            element={<DepartementSelectionPage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Routes publiques */}
      <Route path="/login"           element={<LoginPage />} />
      <Route path="/register"        element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      {/* Routes protégées */}
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <Box sx={{ display: "flex" }}>
              <Navbar />
              <NotificationToastStack />
              <NewDocumentSpeedDial />
              <Box
                component="main"
                sx={{
                  flexGrow: 1,
                  bgcolor: "background.default",
                  p: 3,
                  marginTop: 8,
                }}
              >
                <Routes>
                  {/* Routes compatibles "Tous départements" : pas de wrapper */}
                  <Route path="/" element={<Home />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/article" element={<GestionArticle/>} />
                  <Route path="/article/masse" element={<GestionArticleMasse/>} />
                  <Route path="/StockAndStat" element={<StockAndStat/>} />

                  {/* Routes nécessitant un dept sélectionné : DeptRequiredRoute */}
                  {/* Tiers centralisés : accessibles uniquement en mode "Tous départements" */}
                  <Route path="/client" element={<AllModeRequiredRoute><GestionTiers tiersType={TypeTiers.CLIENT} /></AllModeRequiredRoute>} />
                  <Route path="/fournisseur" element={<AllModeRequiredRoute><GestionTiers tiersType={TypeTiers.FOURNISSEUR} /></AllModeRequiredRoute>} />
                  <Route path="/employe" element={<AllModeRequiredRoute><GestionEmploye /></AllModeRequiredRoute>} />
                  <Route path="/groupebien" element={<DeptRequiredRoute><GestionGropueBien/></DeptRequiredRoute>} />
                  <Route path="/bien" element={<DeptRequiredRoute><GestionBien/></DeptRequiredRoute>} />
                  <Route path="/contratLocation" element={<DeptRequiredRoute><GestionContratLocation/></DeptRequiredRoute>} />
                  <Route path="/facturation" element={<DeptRequiredRoute><DocumentVenteListPage/></DeptRequiredRoute>} />
                  <Route path="/facturation/nouveau" element={<DeptRequiredRoute><DocumentVenteCreatePage/></DeptRequiredRoute>} />
                  <Route path="/facturation/:id" element={<DeptRequiredRoute><DocumentVenteDetailPage/></DeptRequiredRoute>} />
                  <Route path="/comptes" element={<Comptes/>} />
                  <Route path="/caisse" element={<DeptRequiredRoute><GestionCaisse/></DeptRequiredRoute>} />
                  <Route path="/operationsDiverses" element={<DeptRequiredRoute><GestionOperationDiverse/></DeptRequiredRoute>} />
                  <Route path="/pos"    element={<DeptRequiredRoute><PointOfSale/></DeptRequiredRoute>} />
                  <Route path="/options" element={<AllModeRequiredRoute><GestionOptions/></AllModeRequiredRoute>} />
                  <Route path="/aiTokens" element={<AllModeRequiredRoute><GestionAiTokens/></AllModeRequiredRoute>} />
                  <Route path="/parametre_generique" element={<DeptRequiredRoute><GestionGeneriqueData/></DeptRequiredRoute>} />
                  <Route path="/departement" element={<AllModeRequiredRoute><GestionDepartement/></AllModeRequiredRoute>} />
                  <Route path="/roles"             element={<AllModeRequiredRoute><GestionRoles /></AllModeRequiredRoute>} />
                  <Route path="/user-droits"      element={<AllModeRequiredRoute><GestionUserDroits /></AllModeRequiredRoute>} />
                  <Route path="/utilisateurs"     element={<AllModeRequiredRoute><GestionUtilisateurs /></AllModeRequiredRoute>} />
                  <Route path="/reportTemplates"  element={<AllModeRequiredRoute><GestionReportTemplate /></AllModeRequiredRoute>} />
                  <Route path="/rapports"         element={<AllModeRequiredRoute><GestionReportJob /></AllModeRequiredRoute>} />
                  <Route path="/workflows"        element={<AllModeRequiredRoute><GestionWorkflow /></AllModeRequiredRoute>} />
                  <Route path="/workflow-statuts" element={<AllModeRequiredRoute><GestionWorkflowStatuts /></AllModeRequiredRoute>} />
                  <Route path="/immobilisation"   element={<GestionImmobilisations />} />
                </Routes>
              </Box>
            </Box>
          </PrivateRoute>
        }
      />
    </Routes>
  );
};

export default App;
